import { useEffect, useState } from "react";
import { setPage, toPrettyDate, useHub } from "../../../../core/Global";
import { toastSuccess } from "../../../../components/base/Toast";
import { FontAwesomeIcons } from "../../../../core/Types";
import Icon from "../../../../components/base/Icon";
import FlexRow from "../../../../components/base/FlexRow";
import Select from "../../../../components/form/Select";
import TextBox from "../../../../components/form/TextBox";
import {
  PercallKinds, PercallKindSettings, PercallMappingItem, PercallMappingTarget, PercallNewNameActions, PercallOrigins, PercallSettingsData
} from "../models/PercallSettings";


type ListFilter = "all" | "unmapped" | "auto" | "manual"

const ORIGINS: Record<PercallOrigins, { text: string, icon: FontAwesomeIcons, badge: string, hint: string }> = {
  [PercallOrigins.None]: { text: "Not Mapped", icon: "link-slash", badge: "badge-light-danger", hint: "Its jobs come in without one." },
  [PercallOrigins.NameMatch]: { text: "Same Name", icon: "equals", badge: "badge-light-success", hint: "Matched with the one of the same name." },
  [PercallOrigins.DefaultTarget]: { text: "Rule", icon: "wand-magic-sparkles", badge: "badge-light-info", hint: "Sent here by the rule for new names." },
  [PercallOrigins.Created]: { text: "Created", icon: "plus", badge: "badge-light-primary", hint: "Created in Rinvox when Percall first sent it." },
  [PercallOrigins.Manual]: { text: "Manual", icon: "hand-pointer", badge: "badge-light-warning", hint: "Mapped by hand." },
}

const RULE_OPTIONS: { value: PercallNewNameActions, title: string, icon: FontAwesomeIcons }[] = [
  { value: PercallNewNameActions.LeaveEmpty, title: "Leave Empty", icon: "link-slash" },
  { value: PercallNewNameActions.CreateNew, title: "Create", icon: "plus" },
  { value: PercallNewNameActions.MapToDefault, title: "Map To", icon: "arrow-right" },
]

const matchesFilter = (m: PercallMappingItem, filter: ListFilter) =>
  filter === "all"
  || (filter === "unmapped" && !m.targetId)
  || (filter === "auto" && !!m.targetId && m.origin !== PercallOrigins.Manual)
  || (filter === "manual" && m.origin === PercallOrigins.Manual);


/** A job type as "Group ↳ Type" -- the group is what tells two types of the same name apart. */
const TargetText = (p: { item?: PercallMappingTarget }) => (
  <span className="d-inline-flex align-items-center text-truncate">
    {
      p.item?.groupName &&
      <>
        <span className="text-muted">{p.item.groupName}</span>
        <Icon icon="arrow-turn-down-right" size={10} className="mx-2 text-gray-400" />
      </>
    }
    <span>{p.item?.name}</span>
  </span>
)

const TargetSelect = (p: {
  id: string, targets: PercallMappingTarget[], value?: string, placeholder: string, disabled?: boolean, deselect?: boolean,
  onSelect: (val?: string) => void
}) => (
  <Select id={p.id} small block marginBottom={0}
    source={p.targets} dataText="name" dataValue="id"
    searchText={i => `${i.groupName ?? ""} ${i.name ?? ""}`}
    optionTemplate={i => <TargetText item={i} />}
    selectionTemplate={i => <TargetText item={i} />}
    placeholder={p.placeholder} disabled={p.disabled} deselect={p.deselect}
    defaultValue={p.value} value={p.value}
    onSelect={p.onSelect} />
)


interface KindColumnProps {
  kind: PercallKinds
  settings: PercallKindSettings
  groups: { id: string, name: string }[]
  keyword: string
  filter: ListFilter
  onChange: (change: (s: PercallKindSettings) => PercallKindSettings) => void
}

/** One kind -- job types or job sources: its rule for new names and every name received. */
function KindColumn(props: KindColumnProps) {

  const hub = useHub();

  // "Map To" needs a target before it can be saved, so picking it only shows the select until one is chosen.
  const [draftAction, draftActionSet] = useState<PercallNewNameActions>();
  const [savingRule, savingRuleSet] = useState<boolean>(false);
  const [savingId, savingIdSet] = useState<string>();

  const { kind, settings } = props;
  const isType = kind === PercallKinds.JobType;
  const noun = isType ? "job type" : "job source";
  const Nouns = isType ? "Job Types" : "Job Sources";
  const rule = settings.rule;
  const action = draftAction ?? rule.action ?? PercallNewNameActions.LeaveEmpty;

  const saveRule = (next: PercallNewNameActions, targetId?: string, groupId?: string) => {
    savingRuleSet(true);
    hub.Post("/base/PercallSettings/rule", { kind, action: next, targetId, groupId }, res => {
      savingRuleSet(false);
      if (res.success) {
        draftActionSet(undefined);
        props.onChange(s => ({ ...s, rule: res.data }));
        toastSuccess("The rule for new names has been saved.");
      }
    })
  }

  const chooseAction = (next: PercallNewNameActions) => {
    if (savingRule || next === action) return;
    if (next === PercallNewNameActions.MapToDefault) {
      draftActionSet(next);
      return;
    }
    saveRule(next);
  }

  const replaceRow = (row: PercallMappingItem) => {
    props.onChange(s => ({ ...s, mappings: s.mappings.map(m => m.id === row.id ? row : m) }));
  }

  const mapRow = (row: PercallMappingItem, targetId?: string) => {
    if ((targetId ?? "") === (row.targetId ?? "") && row.origin === PercallOrigins.Manual) return;
    savingIdSet(row.id);
    hub.Post("/base/PercallSettings/map", { kind, id: row.id, targetId }, res => {
      savingIdSet(undefined);
      if (res.success) {
        replaceRow(res.data);
        toastSuccess(`"${row.name}" has been mapped.`);
      }
    })
  }

  const backToAutomatic = (row: PercallMappingItem) => {
    savingIdSet(row.id);
    hub.Post("/base/PercallSettings/automatic", { kind, id: row.id }, res => {
      savingIdSet(undefined);
      if (res.success) {
        replaceRow(res.data);
        toastSuccess(`"${row.name}" is mapped automatically again.`);
      }
    })
  }

  const mappings = settings.mappings;
  const unmapped = mappings.filter(m => !m.targetId).length;
  const k = props.keyword.trim().toLowerCase();
  const visible = mappings
    .filter(m => !k || (m.name ?? "").toLowerCase().includes(k) || (m.targetName ?? "").toLowerCase().includes(k)
      || (m.targetGroupName ?? "").toLowerCase().includes(k))
    .filter(m => matchesFilter(m, props.filter));

  return (
    <div className="card h-100">
      <div className="card-header align-items-center min-h-60px">
        <div className="card-title m-0">
          <Icon icon={isType ? "list" : "share-nodes"} size={16} className="me-3 text-primary" />
          <span className="fw-bolder fs-4 text-gray-900">{Nouns}</span>
          <span className="badge badge-light ms-3">{mappings.length}</span>
          {unmapped > 0 && <span className="badge badge-light-danger ms-2">{unmapped} not mapped</span>}
        </div>
      </div>

      <div className="px-8 py-4 bg-light border-bottom">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <span className="fs-7 fw-bold text-gray-700" title={`A new ${noun} is matched with the one of the same name first; this decides what happens when there is none.`}>
            New {noun}s with no match:
          </span>
          <div className="btn-group btn-group-sm">
            {
              RULE_OPTIONS.map(o =>
                <button key={o.value} type="button" disabled={savingRule}
                  className={`btn btn-sm px-3 py-1 ${action === o.value ? "btn-primary" : "btn-white border border-gray-300 text-gray-700"}`}
                  onClick={() => chooseAction(o.value)}>
                  <Icon icon={o.icon} size={10} className="me-1" />
                  {o.title}
                </button>
              )
            }
          </div>
          {savingRule && <Icon icon="spinner-third" spin size={12} className="text-primary" />}

          {
            action === PercallNewNameActions.CreateNew && isType &&
            <div style={{ width: 220 }}>
              <Select id="sel-percall-rule-group" small block marginBottom={0}
                source={props.groups} dataText="name" dataValue="id"
                placeholder="In no group" disabled={savingRule}
                defaultValue={rule.groupId} value={rule.groupId}
                onSelect={val => saveRule(PercallNewNameActions.CreateNew, undefined, val)} />
            </div>
          }

          {
            action === PercallNewNameActions.MapToDefault &&
            <div style={{ width: 260 }}>
              <TargetSelect id={`sel-percall-rule-target-${kind}`} targets={settings.targets}
                placeholder={`Choose a ${noun}`} deselect={false} disabled={savingRule}
                value={rule.action === PercallNewNameActions.MapToDefault ? rule.targetId : undefined}
                onSelect={val => { if (val) saveRule(PercallNewNameActions.MapToDefault, val) }} />
            </div>
          }
        </div>
        {
          draftAction === PercallNewNameActions.MapToDefault &&
          <div className="fs-8 text-warning mt-2">Choose a {noun} to save this rule.</div>
        }
      </div>

      <div className="card-body px-8 py-2">
        {
          mappings.length === 0 ?
            <div className="text-center py-15">
              <Icon icon="inbox" size={32} className="text-gray-300 mb-3" />
              <div className="fs-6 fw-bold text-gray-700">Nothing received yet</div>
              <div className="fs-7 text-muted mt-1">{Nouns} appear here once Percall sends jobs.</div>
            </div>
            : visible.length === 0 ?
              <div className="text-center text-muted fs-7 py-10">No {noun}s match.</div>
              :
              visible.map(row => {
                const origin = ORIGINS[row.origin ?? PercallOrigins.None];
                const saving = savingId === row.id;
                return (
                  <div key={row.id} className="d-flex flex-wrap align-items-center gap-3 py-3 border-bottom border-gray-200 border-bottom-dashed">
                    <div className="flex-grow-1" style={{ minWidth: 160, flexBasis: 160 }}>
                      <div className="fw-bold fs-6 text-gray-900">{row.name}</div>
                      <div className="fs-8 text-muted">
                        {row.jobsCount ?? 0} {row.jobsCount === 1 ? "job" : "jobs"} · {toPrettyDate(row.lastSeenAt)}
                      </div>
                    </div>

                    <div style={{ width: 280, maxWidth: "100%" }}>
                      <TargetSelect id={`sel-percall-${row.id}`} targets={settings.targets}
                        placeholder={`No ${noun}`} disabled={saving} value={row.targetId}
                        onSelect={val => mapRow(row, val)} />
                    </div>

                    <FlexRow style={{ width: 120 }}>
                      {
                        saving ?
                          <Icon icon="spinner-third" spin size={12} className="text-primary" />
                          :
                          <span className={`badge ${origin.badge}`}
                            title={origin.hint + (row.origin === PercallOrigins.Manual && row.mappedByUser ? ` By ${row.mappedByUser.fullName}.` : "")}>
                            <Icon icon={origin.icon} size={10} className="me-1" />
                            {origin.text}
                          </span>
                      }
                      {
                        row.origin === PercallOrigins.Manual && !saving &&
                        <a href="#" className="ms-2 text-gray-500 text-hover-primary" title="Back to automatic mapping"
                          onClick={e => { e.preventDefault(); backToAutomatic(row); }}>
                          <Icon icon="rotate-left" size={12} />
                        </a>
                      }
                    </FlexRow>
                  </div>
                )
              })
        }
      </div>
    </div>
  )
}


/**
 * CRM > Settings > Percall Settings: what a job type or job source Percall sends for the first
 * time becomes, and where every name received so far goes -- job types and job sources side by
 * side.
 *
 * The names only ever come from Percall, so nothing is added or deleted here. A new name is
 * always matched with the job type or source of the same name first; the rule on each column
 * only decides what happens when there is none. Picking a target on a row maps it by hand, which
 * the webhook then follows even when one of the same name exists.
 */
export default function PercallSettings() {

  const hub = useHub();

  const [data, dataSet] = useState<PercallSettingsData>();
  const [keyword, keywordSet] = useState<string>("");
  const [filter, filterSet] = useState<ListFilter>("all");

  useEffect(() => {
    setPage("Percall Settings", "CRM", "CRM Settings");
    hub.Get("/base/PercallSettings/", res => {
      if (res.success) dataSet(res.data);
    })
  }, [])

  const updateKind = (k: "jobTypes" | "jobSources") => (change: (s: PercallKindSettings) => PercallKindSettings) => {
    dataSet(d => d ? { ...d, [k]: change(d[k]) } : d);
  }

  const all = [...(data?.jobTypes.mappings ?? []), ...(data?.jobSources.mappings ?? [])];
  const count = (f: ListFilter) => all.filter(m => matchesFilter(m, f)).length;

  const FilterPill = (p: { value: ListFilter, title: string }) => (
    <button type="button"
      className={`btn btn-sm px-4 py-2 ${filter === p.value ? "btn-primary" : "btn-light"}`}
      onClick={() => filterSet(p.value)}>
      {p.title}
      <span className={`badge ms-2 ${filter === p.value ? "badge-white text-primary" : "badge-secondary"}`}>{count(p.value)}</span>
    </button>
  )

  if (!data) {
    return (
      <div className="card">
        <div className="card-body py-20 text-center text-muted">
          <Icon icon="spinner-third" spin size={24} />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-4 mb-5">
        <div className="d-flex flex-wrap gap-2">
          <FilterPill value="all" title="All" />
          <FilterPill value="unmapped" title="Not Mapped" />
          <FilterPill value="auto" title="Automatic" />
          <FilterPill value="manual" title="Manual" />
        </div>
        <div style={{ width: 300, maxWidth: "100%" }}>
          <TextBox id="txt-percall-search" small icon="magnifying-glass" noMarginBottom
            placeholder="Search names..." onTextChanges={t => keywordSet(t)} />
        </div>
      </div>

      <div className="row g-5 mb-5">
        <div className="col-xl-6">
          <KindColumn kind={PercallKinds.JobType} settings={data.jobTypes} groups={data.jobTypeGroups}
            keyword={keyword} filter={filter} onChange={updateKind("jobTypes")} />
        </div>
        <div className="col-xl-6">
          <KindColumn kind={PercallKinds.JobSource} settings={data.jobSources} groups={data.jobTypeGroups}
            keyword={keyword} filter={filter} onChange={updateKind("jobSources")} />
        </div>
      </div>

      <div className="fs-8 text-muted">
        Choosing a job type or source on a row maps that name by hand from now on; jobs already
        received are not changed. The rule for new names only decides where a brand-new name goes.
      </div>
    </>
  );
}
